import {
	attachEngagedClock,
	createEngagedClock,
	type EngagedClock,
} from '@shared/events';

export type ReadingExit = 'next' | 'back' | 'hidden' | 'closed';

/**
 * One stretch of reading one thing (an article, or the replica pages on
 * screen): it starts when the thing is shown and ends when the reader moves
 * on, hides the tab or closes the page, and reports the engaged time in
 * between. Engaged time is reported once, when the stretch ends; there are
 * no heartbeats.
 *
 * Hiding the tab ends the stretch (`exit: 'hidden'`), because a hidden page
 * may never run again. If the reader comes back, a new stretch of the same
 * thing starts quietly: `onLeave` fires again later, `onEnter` does not, so
 * one opening can have several reads and the reading side sums them.
 */
export interface ReadingSegment<T> {
	/** Shows `subject`, ending whatever was shown with `exit`. */
	enter: (subject: T, exit?: ReadingExit) => void;
	/** Ends the current stretch. */
	leave: (exit: ReadingExit) => void;
	/** The tab was hidden. */
	hide: () => void;
	/** The tab is visible again. */
	show: () => void;
	/** The subject on screen, also while the tab is hidden. */
	current: () => T | undefined;
	/** Engaged time of the stretch in progress, 0 when there is none. */
	elapsed: () => number;
}

export function createReadingSegment<T>({
	onEnter,
	onLeave,
	createClock = () => createEngagedClock(),
	attach = attachEngagedClock,
}: {
	onEnter?: (subject: T) => void;
	onLeave: (subject: T, dwellMs: number, exit: ReadingExit) => void;
	createClock?: () => EngagedClock;
	attach?: (clock: EngagedClock) => () => void;
}): ReadingSegment<T> {
	let subject: T | undefined;
	let open: { clock: EngagedClock; detach: () => void; resumed: boolean } | undefined;

	const begin = (resumed: boolean) => {
		const clock = createClock();
		clock.start();
		open = { clock, detach: attach(clock), resumed };
	};

	const end = (exit: ReadingExit) => {
		if (!open || subject === undefined) {
			return;
		}
		const { clock, detach, resumed } = open;
		open = undefined;
		detach();
		const dwellMs = clock.stop();
		// Coming back to the tab and leaving again without reading adds nothing.
		if (resumed && dwellMs === 0) {
			return;
		}
		onLeave(subject, dwellMs, exit);
	};

	return {
		enter: (next, exit = 'next') => {
			end(exit);
			subject = next;
			onEnter?.(next);
			begin(false);
		},
		leave: (exit) => {
			end(exit);
			subject = undefined;
		},
		hide: () => end('hidden'),
		show: () => {
			if (subject !== undefined && !open) {
				begin(true);
			}
		},
		current: () => subject,
		elapsed: () => open?.clock.elapsed() ?? 0,
	};
}
